import type { CatalogCapabilityDto } from '../domain/adapterService';
import type { CanonicalFieldDef } from '../domain/canonicalFieldDef';
import type { RoutingServiceKey, VendorEndpointMapping, VendorMapping } from '../domain/vendor';
import { isRoutingServiceKey } from '../domain/vendor';
import { LEGACY_CAPABILITY_CATALOG } from './legacyCapabilityCatalog';
import { isAdapterBackedCapability } from './adapterCapabilityPrefixes';

export { isAdapterBackedCapability } from './adapterCapabilityPrefixes';

export interface CapabilityOptionLike {
  serviceName: RoutingServiceKey;
  capability: string;
  label: string;
  endpointTemplate: string;
  schema: string;
  mappingTargets: Array<{ name: string; description: string; required?: boolean }>;
  requestMappingTargets?: Array<{ name: string; description: string; required?: boolean }>;
  adapterServiceKey?: string;
  configLabel?: string;
  serviceDisplayName?: string;
  stepKey?: string | null;
  catalogGroupKey?: string;
  canonicalRequest?: Record<string, unknown>;
  canonicalResponse?: Record<string, unknown>;
}

export interface CatalogServiceGroup {
  key: string;
  adapterServiceKey: string;
  configLabel: string;
  serviceDisplayName: string;
  adminServiceSlug: string;
  serviceName: RoutingServiceKey;
}

export interface RuntimeRequestField {
  path: string;
  label: string;
  description: string;
  required?: boolean;
}

export function catalogGroupKeyFromParts(adapterServiceKey: string, configLabel: string): string {
  return `${adapterServiceKey}:${configLabel}`;
}

export function normalizeConfigLabel(label: string): string {
  const trimmed = label.trim() || 'default';
  return trimmed;
}

/** Empty filterLabel shows all services (service onboarding list). */
export function matchesConfigLabel(serviceLabel: string | undefined, filterLabel: string): boolean {
  if (!filterLabel.trim()) return true;
  return normalizeConfigLabel(serviceLabel ?? 'default') === normalizeConfigLabel(filterLabel);
}

export type VendorLabelSource = {
  adapterConfigLabel?: string;
  capability?: string;
  endpointMappings?: Array<{ capability: string }>;
};

export function labelsForCapabilityInCatalog(
  capability: string,
  catalog: CatalogCapabilityDto[],
): string[] {
  const labels = new Set<string>();
  for (const entry of catalog) {
    if (entry.capability === capability) {
      labels.add(normalizeConfigLabel(entry.configLabel));
    }
  }
  return [...labels];
}

/** Infer adapter_configs.label from published catalog + endpoint capabilities when not stored on vendor. */
export function inferAdapterConfigLabelFromCatalog(
  vendor: VendorLabelSource,
  catalog: CatalogCapabilityDto[],
): string | undefined {
  const capabilities = new Set<string>();
  for (const endpoint of vendor.endpointMappings ?? []) {
    if (endpoint.capability?.trim()) capabilities.add(endpoint.capability.trim());
  }
  if (vendor.capability?.trim()) capabilities.add(vendor.capability.trim());
  if (capabilities.size === 0) return undefined;

  let intersection: Set<string> | null = null;
  for (const cap of capabilities) {
    const labels = labelsForCapabilityInCatalog(cap, catalog);
    if (labels.length === 0) continue;
    const labelSet = new Set(labels);
    if (intersection === null) {
      intersection = labelSet;
    } else {
      intersection = new Set(
        [...intersection].filter((label: string) => labelSet.has(label)),
      );
    }
  }
  if (intersection?.size === 1) return [...intersection][0];
  return undefined;
}

/** Stored label wins; else catalog inference. Never silently defaults to "default". */
export function resolveVendorAdapterConfigLabel(
  vendor: VendorLabelSource,
  catalog?: CatalogCapabilityDto[],
): string | undefined {
  if (vendor.adapterConfigLabel?.trim()) {
    return normalizeConfigLabel(vendor.adapterConfigLabel);
  }
  if (catalog?.length) {
    return inferAdapterConfigLabelFromCatalog(vendor, catalog);
  }
  return undefined;
}

/** Empty filterLabel matches all vendors (provider list "All labels"). */
export function vendorMatchesAdapterConfigLabel(
  vendor: VendorLabelSource,
  filterLabel: string,
  catalog?: CatalogCapabilityDto[],
): boolean {
  if (!filterLabel.trim()) return true;
  const effective = resolveVendorAdapterConfigLabel(vendor, catalog);
  if (!effective) return false;
  return effective === normalizeConfigLabel(filterLabel);
}

export function findCatalogCapabilityEntry(
  catalog: CatalogCapabilityDto[],
  capability: string,
  preferredLabel?: string,
): CatalogCapabilityDto | undefined {
  const matches = catalog.filter(entry => entry.capability === capability);
  if (matches.length === 0) return undefined;
  if (preferredLabel?.trim()) {
    const want = normalizeConfigLabel(preferredLabel);
    const scoped = matches.find(entry => normalizeConfigLabel(entry.configLabel) === want);
    if (scoped) return scoped;
  }
  return matches[0];
}

export function findCapabilityOptionByLabel(
  catalog: CapabilityOptionLike[],
  capability: string,
  preferredLabel?: string,
): CapabilityOptionLike | undefined {
  const matches = catalog.filter(entry => entry.capability === capability);
  if (matches.length === 0) return undefined;
  if (preferredLabel?.trim()) {
    const want = normalizeConfigLabel(preferredLabel);
    const scoped = matches.find(
      entry => normalizeConfigLabel(entry.configLabel ?? 'default') === want,
    );
    if (scoped) return scoped;
  }
  return matches[0];
}

export function resolveCatalogGroupKeyForVendor(
  groups: CatalogServiceGroup[],
  options: {
    preferredCatalogGroupKey?: string;
    loadedService?: string;
    endpointServiceName?: string;
    endpointCapability?: string;
  },
): string | null {
  const preferred = options.preferredCatalogGroupKey?.trim();
  if (preferred && groups.some(g => g.key === preferred)) return preferred;

  if (options.endpointCapability) {
    const adapterKey = resolveAdapterServiceKeyFromEndpoint(
      options.endpointServiceName,
      options.endpointCapability,
    );
    const byAdapter = groups.find(g => g.adapterServiceKey === adapterKey);
    if (byAdapter) return byAdapter.key;
  }

  const loaded = options.loadedService?.trim();
  if (loaded) {
    const byService = groups.find(
      g => g.serviceName === loaded || g.adapterServiceKey === loaded || g.adminServiceSlug === loaded,
    );
    if (byService) return byService.key;
  }

  return groups[0]?.key ?? null;
}

export function filterCatalogEntriesByLabel(
  entries: CatalogCapabilityDto[],
  configLabel: string,
): CatalogCapabilityDto[] {
  const want = normalizeConfigLabel(configLabel);
  return entries.filter(e => normalizeConfigLabel(e.configLabel) === want);
}

/** Normalize stored service slug for routing; accepts any valid routing key from catalog. */
export function normalizeRoutingSlug(value: unknown): RoutingServiceKey | null {
  if (typeof value !== 'string') return null;
  const slug = value.trim().toLowerCase();
  return isRoutingServiceKey(slug) ? slug : null;
}

/** @deprecated Use normalizeRoutingSlug — kept for callers migrating off ServiceName union. */
export function normalizeServiceName(value: unknown): RoutingServiceKey | null {
  return normalizeRoutingSlug(value);
}

function deriveUiSchemaFromCatalogEntry(c: CatalogCapabilityDto): string {
  const slug = c.adminServiceSlug.trim().toLowerCase();
  if (slug === 'employer-verification') return 'employer';
  if (slug === 'income-verification') {
    return c.capability.startsWith('INCOME.PROBE') ? 'income-probe' : 'income';
  }
  if (slug === 'identity-verification') return 'identity';
  return slug;
}

/** Routing slug from published catalog entry (catalog-first; no closed service union). */
export function routingServiceNameForCatalogEntry(c: CatalogCapabilityDto): RoutingServiceKey {
  const slug = c.adminServiceSlug.trim().toLowerCase();
  return (normalizeRoutingSlug(slug) ?? slug) as RoutingServiceKey;
}

function fieldsToTargets(fields: CanonicalFieldDef[]): Array<{ name: string; description: string; required?: boolean }> {
  return fields.map(f => ({
    name: f.path,
    description: f.description ?? `Canonical field ${f.path}`,
    ...(f.required ? { required: true } : {}),
  }));
}

function topLevelKeysFromJson(obj: Record<string, unknown>): Array<{ name: string; description: string }> {
  return Object.keys(obj).map(key => ({
    name: key,
    description: `Canonical response field from Service Onboarding`,
  }));
}

export function catalogEntryToCapabilityOption(c: CatalogCapabilityDto): CapabilityOptionLike {
  const serviceName = routingServiceNameForCatalogEntry(c);
  const schema = deriveUiSchemaFromCatalogEntry(c);

  const requestTargets =
    c.requestMappingTargets?.length > 0
      ? c.requestMappingTargets
      : fieldsToTargets(c.canonicalRequestFields ?? []);

  const responseTargets =
    c.responseMappingTargets?.length > 0
      ? c.responseMappingTargets
      : c.canonicalResponseFields?.length
        ? fieldsToTargets(c.canonicalResponseFields)
        : topLevelKeysFromJson(c.canonicalResponse ?? {});

  return {
    serviceName,
    capability: c.capability,
    label: c.label,
    endpointTemplate: '',
    schema,
    adapterServiceKey: c.adapterServiceKey,
    configLabel: c.configLabel,
    serviceDisplayName: c.serviceDisplayName,
    stepKey: c.stepKey,
    catalogGroupKey: catalogGroupKeyFromParts(c.adapterServiceKey, c.configLabel),
    canonicalRequest: c.canonicalRequest,
    canonicalResponse: c.canonicalResponse,
    requestMappingTargets: requestTargets,
    mappingTargets: responseTargets,
  };
}

export function buildCatalogGroupsForLabel(
  entries: CatalogCapabilityDto[],
  configLabel: string,
): CatalogServiceGroup[] {
  return buildCatalogGroups(filterCatalogEntriesByLabel(entries, configLabel));
}

export function buildCatalogGroups(entries: CatalogCapabilityDto[]): CatalogServiceGroup[] {
  const map = new Map<string, CatalogServiceGroup>();
  for (const e of entries) {
    const key = catalogGroupKeyFromParts(e.adapterServiceKey, e.configLabel);
    if (map.has(key)) continue;
    const opt = catalogEntryToCapabilityOption(e);
    map.set(key, {
      key,
      adapterServiceKey: e.adapterServiceKey,
      configLabel: e.configLabel,
      serviceDisplayName: e.serviceDisplayName,
      adminServiceSlug: e.adminServiceSlug,
      serviceName: opt.serviceName,
    });
  }
  return [...map.values()].sort((a, b) =>
    a.serviceDisplayName.localeCompare(b.serviceDisplayName) || a.configLabel.localeCompare(b.configLabel),
  );
}

export function capabilitiesForGroup(
  groupKey: string,
  catalog: CapabilityOptionLike[],
): CapabilityOptionLike[] {
  return catalog.filter(c => c.catalogGroupKey === groupKey);
}

export function capabilitiesForService(
  serviceName: RoutingServiceKey,
  catalog: CapabilityOptionLike[],
): CapabilityOptionLike[] {
  return catalog.filter(c => c.serviceName === serviceName);
}

export function selectableCapabilitiesForGroup(
  catalog: CapabilityOptionLike[],
  serviceName: RoutingServiceKey,
  groupKey: string | null,
): CapabilityOptionLike[] {
  if (groupKey) return capabilitiesForGroup(groupKey, catalog);
  return capabilitiesForService(serviceName, catalog);
}

function serviceSlugFromCapability(capability: unknown, catalog: CapabilityOptionLike[]): RoutingServiceKey | null {
  if (typeof capability !== 'string') return null;
  const match = catalog.find(c => c.capability === capability);
  return match ? normalizeRoutingSlug(match.serviceName) : null;
}

export type VendorCatalogGroupContext = {
  groups: CatalogServiceGroup[];
  groupKey: string | null;
  group: CatalogServiceGroup | null;
};

export function resolveVendorCatalogGroup(
  vendor: Pick<VendorMapping, 'preferredCatalogGroupKey' | 'endpointMappings' | 'serviceName' | 'capability'>,
  catalogItems: CatalogCapabilityDto[],
  configLabel?: string,
): VendorCatalogGroupContext {
  const label = configLabel?.trim()
    ? normalizeConfigLabel(configLabel)
    : (resolveVendorAdapterConfigLabel(vendor, catalogItems) ?? '');
  const groups = label && catalogItems.length > 0 ? buildCatalogGroupsForLabel(catalogItems, label) : [];
  const firstEndpoint = vendor.endpointMappings?.[0];
  const groupKey = groups.length
    ? resolveCatalogGroupKeyForVendor(groups, {
        preferredCatalogGroupKey: vendor.preferredCatalogGroupKey,
        loadedService: firstEndpoint?.serviceName ?? vendor.serviceName,
        endpointServiceName: firstEndpoint?.serviceName,
        endpointCapability: firstEndpoint?.capability ?? vendor.capability,
      })
    : null;
  const group = groupKey
    ? groups.find(g => g.key === groupKey) ?? null
    : groups[0] ?? null;
  return { groups, groupKey: group?.key ?? groupKey, group };
}

/** Catalog-group-first routing slug for provider onboarding. */
export function resolveVendorRoutingSlug(
  vendor: Pick<VendorMapping, 'preferredCatalogGroupKey' | 'endpointMappings' | 'serviceName' | 'capability'>,
  catalogItems: CatalogCapabilityDto[],
  mergedCatalog: CapabilityOptionLike[],
  endpointHint?: Pick<VendorEndpointMapping, 'serviceName' | 'capability'> | null,
  configLabel?: string,
): RoutingServiceKey {
  const { group } = resolveVendorCatalogGroup(vendor, catalogItems, configLabel);
  if (group) {
    return normalizeRoutingSlug(group.adminServiceSlug) ?? group.serviceName;
  }

  const firstEndpoint = vendor.endpointMappings?.[0];
  const fromStored =
    normalizeRoutingSlug(endpointHint?.serviceName) ??
    normalizeRoutingSlug(firstEndpoint?.serviceName) ??
    normalizeRoutingSlug(vendor.serviceName);
  if (fromStored) return fromStored;

  const capability =
    endpointHint?.capability ?? vendor.capability ?? firstEndpoint?.capability;
  const fromCapability = serviceSlugFromCapability(capability, mergedCatalog);
  if (fromCapability) return fromCapability;

  const legacy = mergedCatalog[0];
  if (legacy) return normalizeRoutingSlug(legacy.serviceName) ?? legacy.serviceName;

  return 'income-verification';
}

/** @deprecated Use resolveVendorRoutingSlug */
export function resolveVendorLoadedService(
  vendor: Pick<VendorMapping, 'preferredCatalogGroupKey' | 'serviceName' | 'capability' | 'endpointMappings'>,
  catalog: CapabilityOptionLike[],
  endpointHint?: Pick<VendorEndpointMapping, 'serviceName' | 'capability'> | null,
  catalogItems: CatalogCapabilityDto[] = [],
): RoutingServiceKey {
  return resolveVendorRoutingSlug(vendor, catalogItems, catalog, endpointHint);
}

export interface VendorAddApiContext {
  canAdd: boolean;
  configured: number;
  total: number;
  groupKey: string | null;
  serviceName: RoutingServiceKey;
}

export function vendorAddApiDisabledMessage(ctx: Pick<VendorAddApiContext, 'configured' | 'total'>): string {
  return `All capabilities for this service are configured (${ctx.configured}/${ctx.total})`;
}

export function vendorAddApiContext(
  vendor: VendorMapping,
  catalogItems: CatalogCapabilityDto[],
): VendorAddApiContext {
  const label = resolveVendorAdapterConfigLabel(vendor, catalogItems) ?? '';
  const mergedCatalog = mergeAdapterCatalog(LEGACY_CAPABILITY_CATALOG, catalogItems);
  const { groupKey } = resolveVendorCatalogGroup(vendor, catalogItems, label);
  const firstEndpoint = vendor.endpointMappings?.[0];
  const serviceName = resolveVendorRoutingSlug(
    vendor,
    catalogItems,
    mergedCatalog,
    firstEndpoint ?? null,
    label,
  );
  const pool = selectableCapabilitiesForGroup(mergedCatalog, serviceName, groupKey);
  const mappings = vendor.endpointMappings ?? [];
  const unclaimed = pool.filter(
    option =>
      !endpointMappingClaimsCapability(
        mappings,
        option.capability,
        serviceName,
        adapterEndpointServiceName(option),
      ),
  );
  return {
    canAdd: unclaimed.length > 0,
    configured: pool.length - unclaimed.length,
    total: pool.length,
    groupKey,
    serviceName,
  };
}

function endpointMappingClaimsCapability(
  mappings: VendorEndpointMapping[],
  capability: string,
  serviceName: string,
  adapterServiceKey: string,
): boolean {
  const service = serviceName.trim();
  const adapterKey = adapterServiceKey.trim();
  return mappings.some(item => {
    if (item.capability !== capability) return false;
    const stored = (item.serviceName ?? '').trim();
    return stored === service || stored === adapterKey;
  });
}

export function mergeAdapterCatalog<T extends CapabilityOptionLike>(
  baseCatalog: T[],
  catalogEntries: CatalogCapabilityDto[],
): T[] {
  const fromApi = catalogEntries.map(catalogEntryToCapabilityOption) as T[];
  const apiCaps = new Set(fromApi.map(c => c.capability));
  // Keep non-adapter legacy entries, plus adapter bootstrap entries not yet
  // published via Service Onboarding (e.g. DE.PROVENIR_EVALUATE).
  const fromBase = baseCatalog.filter(c => {
    if (!isAdapterBackedCapability(c.capability)) return true;
    return !apiCaps.has(c.capability);
  });
  return [...fromBase, ...fromApi];
}

export function adapterEndpointServiceName(option: CapabilityOptionLike): string {
  return option.adapterServiceKey ?? option.serviceName;
}

export function runtimeRequestFieldsFromTargets(
  targets: Array<{ name: string; description: string; required?: boolean }> | undefined,
): RuntimeRequestField[] {
  if (!targets?.length) return [];
  return targets.map(t => ({
    path: t.name,
    label: t.name,
    description: t.description || `Adapter payload field p.${t.name}`,
    required: Boolean(t.required),
  }));
}

export function payloadPathToJsonata(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (trimmed === 'p' || trimmed.startsWith('p.')) return trimmed;
  return `p.${trimmed}`;
}

/** Build minimal nested JSON from dot paths for mapping preview. */
export function samplePayloadFromFieldPaths(paths: string[]): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const path of paths) {
    const parts = path.split('.').filter(Boolean);
    if (parts.length === 0) continue;
    let cursor: Record<string, unknown> = root;
    for (let i = 0; i < parts.length; i++) {
      const key = parts[i];
      const isLeaf = i === parts.length - 1;
      if (isLeaf) {
        cursor[key] = `sample-${key}`;
      } else {
        if (cursor[key] == null || typeof cursor[key] !== 'object' || Array.isArray(cursor[key])) {
          cursor[key] = {};
        }
        cursor = cursor[key] as Record<string, unknown>;
      }
    }
  }
  return root;
}

export function samplePayloadForCapability(option: CapabilityOptionLike | undefined): Record<string, unknown> {
  if (!option) {
    return { requestId: 'preview-request-id' };
  }
  if (option.canonicalRequest && Object.keys(option.canonicalRequest).length > 0) {
    return option.canonicalRequest;
  }
  const paths = option.requestMappingTargets?.map(t => t.name) ?? [];
  if (paths.length > 0) {
    return samplePayloadFromFieldPaths(paths);
  }
  return { requestId: 'preview-request-id' };
}

/** Realistic adapter payload for JSONata preview (avoids catalog type placeholders like "string"). */
export function realisticSamplePayloadForCapability(
  option: CapabilityOptionLike | undefined,
  providerVariables: Record<string, string> = {},
): Record<string, unknown> {
  if (option?.capability === 'AI.GENERATE') {
    return {
      model: providerVariables.model?.trim() || 'gemini-2.5-flash',
      messages: [{ role: 'user', content: 'Hello' }],
      useWebSearch: false,
    };
  }
  const base = samplePayloadForCapability(option);
  if (providerVariables.model && typeof base.model === 'string') {
    return { ...base, model: providerVariables.model };
  }
  return base;
}

export function catalogContextLabel(option: CapabilityOptionLike | undefined): string | null {
  if (!option?.serviceDisplayName) return null;
  const config = option.configLabel ?? 'default';
  return `Service: ${option.serviceDisplayName} · config: ${config} · ${option.capability}`;
}

/** Align with admin-api resolveAdapterServiceKey for routing / endpoint filters. */
export function resolveAdapterServiceKeyFromEndpoint(
  serviceName: string | undefined,
  capability: string,
): string {
  const cap = capability.trim();
  if (cap === 'AI.GENERATE') return 'ai-gateway';
  if (cap.startsWith('DE.')) return 'decision-engine';
  const svc = (serviceName ?? '').trim().toLowerCase();
  if (svc) return svc;
  if (cap.startsWith('INCOME.GET_TX_')) return 'income-verification';
  if (cap.startsWith('IDENTITY.TPA_')) return 'identity-verification';
  return (serviceName ?? '').trim();
}

export function endpointMatchesRoutingKey(
  endpoint: { serviceName?: string; capability: string },
  routingKey: string,
): boolean {
  const resolved = resolveAdapterServiceKeyFromEndpoint(endpoint.serviceName, endpoint.capability);
  return resolved === routingKey || endpoint.serviceName === routingKey;
}
